import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import RatingStars from "@/components/base/RatingStars";
import RelatedMovies from "@/pages/detail/components/RelatedMovies";
import ReviewList from "@/pages/detail/components/ReviewList";
import { getMovieById, getMoviesByIds, movies } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";
import { useBrowseHistory } from "@/hooks/useBrowseHistory";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = id ? getMovieById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const historyIds = useBrowseHistory(movie?.id);
  const [copied, setCopied] = useState(false);

  usePageMeta(
    movie
      ? `${movie.title}（${movie.year}） - 详细介绍与影评 | 80s电影`
      : "影片未找到 | 80s电影",
    movie ? movie.synopsis.slice(0, 120) : undefined,
    movie ? `${movie.title},${movie.originalTitle},80s电影,80年代电影` : undefined
  );

  if (!movie) {
    return (
      <PageLayout>
        <div className="mx-auto flex min-h-[50vh] w-full max-w-3xl flex-col items-center justify-center px-4 text-center">
          <i className="ri-film-line mb-4 flex h-16 w-16 items-center justify-center text-4xl text-foreground-300"></i>
          <h1 className="font-heading text-xl font-bold text-foreground-950">
            没有找到这部影片
          </h1>
          <p className="mt-2 font-label text-sm text-foreground-600">
            它可能已被下架，或者链接有误。
          </p>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50"
          >
            浏览全部电影
          </Link>
        </div>
      </PageLayout>
    );
  }

  const related = movies
    .filter(
      (m) =>
        m.id !== movie.id &&
        (m.director === movie.director ||
          m.genres.some((g) => movie.genres.includes(g)))
    )
    .slice(0, 5);

  const historyMovies = getMoviesByIds(historyIds).slice(0, 5);
  const favorite = isFavorite(movie.id);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <PageLayout>
      <div className="relative w-full bg-primary-950">
        {movie.backdrop && (
          <div className="absolute inset-0">
            <img
              src={movie.backdrop}
              alt=""
              className="h-full w-full object-top opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/60"></div>
          </div>
        )}
        <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-10 md:flex-row md:px-6 md:py-14">
          <div className="w-40 shrink-0 overflow-hidden rounded-lg border-2 border-background-50/20 shadow-none sm:w-52 md:w-60">
            <img
              src={movie.poster}
              alt={`${movie.title}（${movie.year}）海报`}
              title={`${movie.title} 80年代经典电影海报`}
              className="aspect-[2/3] h-full w-full object-top"
            />
          </div>
          <div className="min-w-0 flex-1 text-background-50">
            <nav className="mb-2 flex flex-wrap items-center gap-1.5 font-label text-xs text-background-300">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="hover:text-accent-300"
              >
                返回
              </button>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category" className="hover:text-accent-300">
                电影分类
              </Link>
            </nav>
            <h1 className="font-heading text-2xl font-black leading-tight md:text-4xl">
              {movie.title}
              <span className="ml-2 font-label text-lg font-normal text-background-300">
                ({movie.year})
              </span>
            </h1>
            <p className="mt-1 font-label text-sm text-background-300">
              {movie.originalTitle}
            </p>
            <p className="mt-3 font-heading text-base italic text-accent-300 md:text-lg">
              {movie.tagline}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <RatingStars value={movie.rating} size="md" />
              <span className="h-4 w-px bg-background-100/30"></span>
              <span className="font-label text-sm text-background-200">
                {movie.runtime} 分钟
              </span>
              <span className="h-4 w-px bg-background-100/30"></span>
              <span className="font-label text-sm text-background-200">
                {movie.region}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {movie.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category?genre=${encodeURIComponent(genre)}`}
                  className="rounded-md bg-background-50/10 px-2.5 py-1 font-label text-xs text-background-100 transition-colors hover:bg-accent-500 hover:text-background-50"
                >
                  {genre}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => toggleFavorite(movie.id)}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 font-label text-sm font-semibold transition-colors ${
                  favorite
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-background-100/40 text-background-50 hover:bg-background-50/10"
                }`}
              >
                <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                {favorite ? "已收藏" : "收藏影片"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-share-forward-line"></i>
                {copied ? "链接已复制" : "分享影片"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl space-y-10 px-4 py-10 md:px-6">
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                剧情简介
              </h2>
              <p className="mt-3 font-label text-sm leading-relaxed text-foreground-700 md:text-base">
                {movie.synopsis}
              </p>
            </div>

            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                影评精选
              </h2>
              <div className="mt-3">
                <ReviewList reviews={movie.reviews} />
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                基本信息
              </h2>
              <dl className="mt-4 space-y-3 font-label text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">导演</dt>
                  <dd className="text-right text-foreground-900">
                    {movie.director}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-right text-foreground-900">
                    {movie.cast.map((actor, idx) => (
                      <span key={actor}>
                        <Link
                          to={`/category?cast=${encodeURIComponent(actor)}`}
                          className="hover:text-accent-600"
                        >
                          {actor}
                        </Link>
                        {idx < movie.cast.length - 1 ? "、" : ""}
                      </span>
                    ))}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">类型</dt>
                  <dd className="text-right text-foreground-900">
                    {movie.genres.join(" / ")}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">地区</dt>
                  <dd className="text-foreground-900">{movie.region}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">上映时间</dt>
                  <dd className="text-foreground-900">{movie.year} 年</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">片长</dt>
                  <dd className="text-foreground-900">{movie.runtime} 分钟</dd>
                </div>
              </dl>
            </div>

            {historyMovies.length > 0 && (
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  最近浏览
                </h2>
                <div className="mt-3 space-y-3">
                  {historyMovies.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group flex items-center gap-3"
                    >
                      <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md bg-primary-950">
                        <img
                          src={item.poster}
                          alt={item.title}
                          className="h-full w-full object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-label text-sm text-foreground-900 line-clamp-1 group-hover:text-accent-600">
                          {item.title}
                        </p>
                        <p className="font-label text-xs text-foreground-500">
                          {item.year}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </section>

        <RelatedMovies movies={related} />
      </div>
    </PageLayout>
  );
}